'use client';

import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { Badge, Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ROUTES } from '@/config/routes';
import type { Product } from '@/data/products';
import { formatPrice } from '@/lib/format';

export interface ProductCardProps {
  product: Product;
  /** Hides the order CTA for bundled items such as the free app. */
  orderable?: boolean;
}

export function ProductCard({ product, orderable = true }: ProductCardProps) {
  const isFree = product.priceUsd === 0;

  return (
    <Card flush className="flex h-full flex-col gap-6" highlighted={product.featured} interactive>
      {product.image ? (
        <div className="bg-ink/60 relative h-56 overflow-hidden rounded-t-3xl sm:h-64">
          <OptimizedImage
            src={product.image}
            alt={product.imageAlt ?? product.name}
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
            className="object-cover object-top"
          />
          {product.featured ? (
            // Opaque backing: the badge tint alone washes out over bright artwork.
            <div className="bg-ink/85 absolute top-4 left-4 rounded-full backdrop-blur">
              <Badge tone="brand">Most popular</Badge>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={`flex flex-1 flex-col gap-5 px-6 pb-8 sm:px-8 ${product.image ? '' : 'pt-8'}`}>
        <div className="flex flex-col gap-2">
          <p className="text-brand-bright text-xs font-semibold tracking-[0.22em] uppercase">{product.tagline}</p>
          <h3 className="text-2xl text-white">{product.name}</h3>
          <p className="text-content-muted text-sm leading-relaxed">{product.description}</p>
        </div>

        <div className="border-line/70 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-y py-4">
          <span className={`font-display text-4xl font-bold ${isFree ? 'text-success' : 'text-white'}`}>
            {formatPrice(product.priceUsd)}
          </span>
          <span className="text-content-subtle text-sm">{product.priceNote}</span>
        </div>

        <ul className="flex flex-col gap-2.5">
          {product.highlights.map((highlight) => (
            <li key={highlight} className="text-content-muted flex items-start gap-2.5 text-sm">
              <Icon name="check" className="text-brand-bright mt-0.5 h-4 w-4" />
              <span>{highlight}</span>
            </li>
          ))}
        </ul>

        {product.addOns.length > 0 ? (
          <div className="border-line-strong/60 bg-ink/40 rounded-2xl border p-4">
            <p className="text-content-subtle text-xs font-semibold tracking-wider uppercase">Optional add-ons</p>
            <ul className="mt-2 flex flex-col gap-2">
              {product.addOns.map((addOn) => (
                <li key={addOn.id} className="text-content-muted text-sm">
                  <span className="text-white">{addOn.name}</span> — {formatPrice(addOn.priceUsd)}
                  <span className="text-content-subtle block text-xs">{addOn.description}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {orderable ? (
          <Button
            className="mt-auto"
            fullWidth
            variant={product.featured ? 'primary' : 'secondary'}
            to={`${ROUTES.buy}?product=${product.id}`}
          >
            Order {product.name}
            <Icon name="arrow" className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
